import { useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, FolderOpen, Sparkles } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { useAdminNiches } from '@/hooks/useAdmin';
import { useSources } from '@/hooks/useInsights';
import type { SourceFolderRow, SourceNicheRow } from '@/api/insights';
import { env } from '@/lib/env';
import { insightsInvitesHref, JOINED_STAGES, money, POSTED_STAGES, REPLIED_STAGES, toNum } from '@/lib/insights';
import { useInsightsScope } from './scope';
import {
  CountLink,
  LoadError,
  Loading,
  MetricLabel,
  RateValue,
  Section,
  SortHeader,
  useSort,
  useSorted,
} from './parts';

type FolderKey = 'name' | 'exported' | 'messaged' | 'replied' | 'joined' | 'posted' | 'cashPaid' | 'joinRate' | 'postRate';
type NicheKey = 'niche' | 'replied' | 'joined' | 'posted' | 'joinRate' | 'postRate' | 'earned';

const FALLBACK_DEFS: Record<string, string> = {
  exported: 'Leads sent from this discovery folder to the outreach queue.',
  messages: 'Of those leads, how many were messaged.',
  replied: 'Replies in this period that came from a lead in this folder.',
  joinedCohort: 'Of those replies, how many have joined since.',
  postedCohort: 'Of those replies, how many have posted since.',
  folderCashPaid: 'Cash paid in this period on invites from this folder.',
  joinRate: 'Joined ÷ replied, tracked teams only.',
  postRate: 'Posted ÷ replied, tracked teams only.',
  earned: 'What replies with this niche (their first niche) have earned so far.',
  unlinkedReplied: 'Replies in this period with no discovery lead behind them.',
};

const folderName = (f: SourceFolderRow): string => f.name ?? 'Unnamed folder';

/**
 * Most replies aren't linked to a folder yet, so the folder table covers a
 * minority of them. Said up front, with the share, so nobody reads the
 * folders as the whole story.
 */
function UnlinkedGap({ unlinked, linked, def }: { unlinked: number; linked: number; def?: string }): JSX.Element | null {
  if (unlinked === 0) return null;
  const total = unlinked + linked;
  const share = total > 0 ? Math.round((unlinked / total) * 100) : 100;
  return (
    <div role="note" className="panel flex items-start gap-3 rounded-xl border-warning/40 p-4">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-pending-strong" aria-hidden />
      <div className="text-sm">
        <p className="font-semibold">
          {unlinked.toLocaleString()} of {total.toLocaleString()} replies in this period ({share}%){' '}
          <MetricLabel label="aren’t linked to any folder" def={def} />.
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          They were logged by hand with no discovery lead behind them, so the folders below only account for{' '}
          {linked.toLocaleString()} {linked === 1 ? 'reply' : 'replies'}. Treat folder totals as a floor, not the whole
          picture.
        </p>
      </div>
    </div>
  );
}

export function SourcesTab(): JSX.Element {
  const scope = useInsightsScope();
  const q = useSources(scope.params);
  const niches = useAdminNiches();
  const [folderSort, toggleFolder] = useSort<FolderKey>({ key: 'posted', dir: 'desc' });
  const [nicheSort, toggleNiche] = useSort<NicheKey>({ key: 'replied', dir: 'desc' });
  const nicheName = useMemo(() => {
    const map = new Map((niches.data ?? []).map((n) => [n.code, n.displayName]));
    return (code: string) => map.get(code) ?? code;
  }, [niches.data]);

  const folders = useSorted(q.data?.folders ?? [], folderSort, (r: SourceFolderRow, k) =>
    k === 'name' ? folderName(r).toLowerCase() : k === 'cashPaid' ? toNum(r.cashPaid) : r[k],
    (r, k) => (k === 'joinRate' || k === 'postRate' ? r.replied : undefined),
  );
  const nicheRows = useSorted(q.data?.niches ?? [], nicheSort, (r: SourceNicheRow, k) =>
    k === 'niche' ? nicheName(r.niche).toLowerCase() : k === 'earned' ? toNum(r.earned) : r[k],
    (r, k) => (k === 'joinRate' || k === 'postRate' ? r.replied : undefined),
  );

  if (q.isLoading) return <Loading label="Loading sources…" />;
  if (q.isError || !q.data) return <LoadError error={q.error} what="sources" />;

  const d = q.data;
  const def = (key: string): string | undefined => d.definitions?.[key] ?? FALLBACK_DEFS[key];
  const window = scope.windows.current;
  const team = scope.team || undefined;
  const linked = d.folders.reduce((s, f) => s + f.replied, 0);

  return (
    <div className="space-y-6">
      <Section
        title="Which discovery folders produce posts?"
        description="Each folder’s leads from export to post. Replied, joined and posted follow this period’s replies forward."
      >
        <UnlinkedGap unlinked={d.unlinkedReplied} linked={linked} def={def('unlinkedReplied')} />
        {folders.length === 0 ? (
          <EmptyState
            icon={<FolderOpen className="h-5 w-5" />}
            title="No replies came from a discovery folder in this period"
            hint="Folder results show once leads exported to outreach are messaged and reply."
          />
        ) : (
          <div className="table-frame relative overflow-x-auto">
            <table className="responsive-stack w-full whitespace-nowrap text-sm" aria-label="Discovery folders">
              <thead>
                <tr className="text-left">
                  <SortHeader label="Folder" k="name" sort={folderSort} onSort={toggleFolder} def={def('folder')} numeric={false} />
                  <SortHeader label="Exported" k="exported" sort={folderSort} onSort={toggleFolder} def={def('exported')} />
                  <SortHeader label="Messaged" k="messaged" sort={folderSort} onSort={toggleFolder} def={def('messages')} />
                  <SortHeader label="Replied" k="replied" sort={folderSort} onSort={toggleFolder} def={def('replied')} />
                  <SortHeader label="Joined" k="joined" sort={folderSort} onSort={toggleFolder} def={def('joinedCohort')} />
                  <SortHeader label="Posted" k="posted" sort={folderSort} onSort={toggleFolder} def={def('postedCohort')} />
                  <SortHeader label="Cash paid" k="cashPaid" sort={folderSort} onSort={toggleFolder} def={def('folderCashPaid')} />
                  <SortHeader label="Join rate" k="joinRate" sort={folderSort} onSort={toggleFolder} def={def('joinRate')} />
                  <SortHeader label="Post rate" k="postRate" sort={folderSort} onSort={toggleFolder} def={def('postRate')} />
                </tr>
              </thead>
              <tbody>
                {folders.map((f) => {
                  // The Invites page filters folders by name, so an unnamed one can't drill down.
                  const drill = f.name ? { team, window, source: f.name } : null;
                  const to = (stages: typeof REPLIED_STAGES): string | null =>
                    drill ? insightsInvitesHref({ ...drill, stages }) : null;
                  return (
                    <tr key={f.folderPublicId ?? `name:${f.name ?? ''}`} className="border-t border-border transition-colors hover:bg-accent/40">
                      <td className="px-3 py-2 font-medium">
                        <span className="inline-flex items-center gap-2">
                          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          {env.discoveryApiUrl && f.folderPublicId ? (
                            <Link
                              to={`/admin/discovery/folders/${f.folderPublicId}`}
                              className={f.name ? 'hover:underline' : 'italic text-muted-foreground hover:underline'}
                            >
                              {folderName(f)}
                            </Link>
                          ) : (
                            folderName(f)
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono" data-label="Exported">
                        {f.exported.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono" data-label="Messaged">
                        {f.messaged.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Replied">
                        <CountLink value={f.replied} to={to(REPLIED_STAGES)} />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Joined">
                        <CountLink value={f.joined} to={to(JOINED_STAGES)} />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Posted">
                        <CountLink value={f.posted} to={to(POSTED_STAGES)} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono" data-label="Cash paid">
                        {money(f.cashPaid)}
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Join rate">
                        <RateValue rate={f.joinRate} n={f.replied} reason="No tracked replies from this folder" />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Post rate">
                        <RateValue rate={f.postRate} n={f.replied} reason="No tracked replies from this folder" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Which niches convert?"
        description="Replies in this period by their first niche, followed forward to joins and posts."
      >
        {nicheRows.length === 0 ? (
          <EmptyState
            icon={<Sparkles className="h-5 w-5" />}
            title="No replies with a niche in this period"
            hint="Pick a longer period, or another team."
          />
        ) : (
          <div className="table-frame relative overflow-x-auto">
            <table className="responsive-stack w-full whitespace-nowrap text-sm" aria-label="Niches">
              <thead>
                <tr className="text-left">
                  <SortHeader label="Niche" k="niche" sort={nicheSort} onSort={toggleNiche} def={def('niche')} numeric={false} />
                  <SortHeader label="Replied" k="replied" sort={nicheSort} onSort={toggleNiche} def={def('replied')} />
                  <SortHeader label="Joined" k="joined" sort={nicheSort} onSort={toggleNiche} def={def('joinedCohort')} />
                  <SortHeader label="Posted" k="posted" sort={nicheSort} onSort={toggleNiche} def={def('postedCohort')} />
                  <SortHeader label="Join rate" k="joinRate" sort={nicheSort} onSort={toggleNiche} def={def('joinRate')} />
                  <SortHeader label="Post rate" k="postRate" sort={nicheSort} onSort={toggleNiche} def={def('postRate')} />
                  <SortHeader label="Earned" k="earned" sort={nicheSort} onSort={toggleNiche} def={def('earned')} />
                </tr>
              </thead>
              <tbody>
                {nicheRows.map((n) => {
                  const drill = { team, window, niche: n.niche || undefined };
                  return (
                    <tr key={n.niche || '(none)'} className="border-t border-border transition-colors hover:bg-accent/40">
                      <td className="px-3 py-2 font-medium">
                        <span className="inline-flex items-center gap-2">
                          <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                          {n.niche ? nicheName(n.niche) : 'No niche'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Replied">
                        <CountLink value={n.replied} to={n.niche ? insightsInvitesHref({ ...drill, stages: REPLIED_STAGES }) : null} />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Joined">
                        <CountLink value={n.joined} to={n.niche ? insightsInvitesHref({ ...drill, stages: JOINED_STAGES }) : null} />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Posted">
                        <CountLink value={n.posted} to={n.niche ? insightsInvitesHref({ ...drill, stages: POSTED_STAGES }) : null} />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Join rate">
                        <RateValue rate={n.joinRate} n={n.replied} reason="No tracked replies with this niche" />
                      </td>
                      <td className="px-3 py-2 text-right" data-label="Post rate">
                        <RateValue rate={n.postRate} n={n.replied} reason="No tracked replies with this niche" />
                      </td>
                      <td className="px-3 py-2 text-right font-mono" data-label="Earned">
                        {money(n.earned)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
